import * as React from "react"
import { useAppToast } from "@ui-seeker/app"
import { postSubmit } from "@ui-seeker/utils"

import { AutocompleteAsyncDebounced } from "@components"
import { BACKEND_APIS } from "@constants"
import { selectedBrands } from "../@signals"


/** "Dior, gucci , DIOR,," -> ["Dior", "gucci"]: trimmed, no blanks, no case-insensitive repeats. */
const splitBrandNames = text => {
    const seen = new Set()
    return text.split(',').map(name => name.trim()).filter(name => {
        const key = name.toLowerCase()
        if (!name || seen.has(key)) return false
        seen.add(key)
        return true
    })
}

function ResolveSummary({ added, ambiguous, notFound }) {
    return <div>
        {added > 0 && <div>{added} brand{added === 1 ? '' : 's'} added.</div>}
        {ambiguous.length > 0 && <div>
            Several matches, pick them from the list: {ambiguous.map(a => `${a.term} (${a.candidates.join(', ')})`).join('; ')}
        </div>}
        {notFound.length > 0 && <div>Not found: {notFound.join(', ')}</div>}
    </div>
}


/**
 * Brands filter of Products Review. Besides the usual search, a comma-separated list
 * ("Armani, Calvin Klein, Dior") + Enter adds every brand the backend can identify
 * without guessing (`/brand/resolve-names`), and says which names were ambiguous or
 * not found (869f904yd).
 *
 * The input text is controlled here because the list has to be cleared after resolving,
 * and AutocompleteAsyncSeeker keeps it in an internal signal otherwise. That takes over
 * the three resets it did on its own (typing, picking an option, blur) — `reset` input
 * changes never reach `onInputChange`, so selection and blur clear it explicitly.
 */
export default function ProductsReviewBrandsFilter() {
    const { showToast } = useAppToast()
    const [inputValue, setInputValue] = React.useState('')
    const [resolving, setResolving] = React.useState(false)
    // A comma list never matches an option, so the dropdown would only say "No options":
    // tell the user what Enter will do instead.
    const pastedNames = inputValue.includes(',') ? splitBrandNames(inputValue) : []

    const resolveBrands = async names => {
        setResolving(true)
        try {
            const { found, ambiguous, not_found } = await postSubmit(
                `${BACKEND_APIS.BRANDS}/crud/brand/resolve-names`, { names }
            )
            const current = selectedBrands.value || []
            const added = found.map(brand => brand.value).filter(id => !current.includes(id))
            // The value BEFORE clearing the input: the clear is what triggers the options
            // fetch, and only ids already selected by then come back in it as chips.
            selectedBrands.value = [...current, ...added]
            setInputValue('')

            const summary = <ResolveSummary added={added.length} ambiguous={ambiguous} notFound={not_found} />
            if (ambiguous.length || not_found.length) showToast.warning(summary, { autoClose: 10000 })
            else showToast.success(summary)
        } catch (error) {
            console.error('Error resolving brand names:', error)
            showToast.error('Error resolving the brand list')
        } finally {
            setResolving(false)
        }
    }

    const handleKeyDown = event => {
        if (event.key !== 'Enter' || !inputValue.includes(',')) return
        // A comma list never matches an option: keep MUI from acting on this Enter.
        event.defaultMuiPrevented = true
        event.preventDefault()
        if (pastedNames.length && !resolving) resolveBrands(pastedNames)
    }

    return <AutocompleteAsyncDebounced
        multiple
        urlAjax={`${BACKEND_APIS.BRANDS}/crud/brand/find-options`}
        label="Brands"
        placeholder="Search Brands, or paste a list: Dior, Gucci…"
        value={selectedBrands.value}
        onChange={e => {
            selectedBrands.value = e.target.value
            setInputValue('')
        }}
        inputValue={inputValue}
        onInputChange={(_, value) => setInputValue(value)}
        onBlur={() => setInputValue('')}
        onKeyDown={handleKeyDown}
        noOptionsText={resolving
            ? 'Looking up the brands…'
            : pastedNames.length
                ? `Press Enter to add ${pastedNames.length} brand${pastedNames.length === 1 ? '' : 's'}`
                : 'No options'}
        queryParam="search"
    />
}
